import { useEffect, useRef, useState } from 'react';
import { JSNS, type ZimbraClient } from '@zimbra-webif/sdk';
import { useIsMobile } from '../lib/useIsMobile';
import { useClient } from '../lib/ClientContext';
import { useSaveDraft, useSend, useSignature, type SendVars } from '../lib/queries';
import { uploadFile, type UploadedFile } from '../lib/upload';
import { fmtDay, fmtSize } from '../lib/format';
import { delingBlockHtml, getDelingConfig, revokeDelingLink, uploadToDeling } from '../lib/deling';

/** A file sent as a Deling (share.mo2.dk) link instead of an attachment. */
interface DelingItem {
  id: number;
  file: File;
  pct: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
  shareId?: string;
  linkDays?: number;
  abort?: () => void;
}
let delingSeq = 0;

export interface ComposeInit {
  title?: string;
  to?: string;
  cc?: string;
  subject?: string;
  /** quoted reply/forward text (plain) seeded into the editor */
  text?: string;
  /** quoted reply/forward content as ready HTML (preferred over `text`) */
  quotedHtml?: string;
  inReplyTo?: { origId: string; replyType: 'r' | 'w' };
  /** attachments already in the thread being replied to / forwarded, offered for inclusion */
  origAttachments?: OrigAttachment[];
  /** reopening a saved draft: its id (Save overwrites it, Send removes it) and its body exactly as saved */
  draftId?: string;
  draftHtml?: string;
}

/** An attachment of a message in the original thread, referenced by id + MIME part (no re-upload). */
export interface OrigAttachment {
  messageId: string;
  part: string;
  filename: string;
  size?: number;
  /** sender of the message carrying it (shown when the thread has several) */
  from?: string;
  date?: number;
}

function parseAddrs(s: string): Array<{ address: string }> {
  return s
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean)
    .map((address) => ({ address }));
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Split a Zimbra autocomplete `email` ("Name" <addr>) into name + address. */
function parseMatch(email: string): { name: string; addr: string } {
  const m = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(email);
  if (m) return { name: (m[1] ?? '').trim(), addr: (m[2] ?? '').trim() };
  return { name: '', addr: email.trim() };
}

interface Suggestion {
  name: string;
  addr: string;
  group: boolean;
}

/** Address input with live AutoComplete (contacts + GAL) dropdown. Recipients are
 *  kept as a comma-separated string; completion replaces the token being typed. */
function RecipientField({
  value,
  onChange,
  placeholder,
  autoFocus,
  client,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  client: ZimbraClient;
}) {
  const [matches, setMatches] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const lastToken = (v: string) => v.slice(v.lastIndexOf(',') + 1).trim();

  function runQuery(v: string) {
    // strip a leading '@' so a domain-style token (e.g. "@uggerly") still queries
    const tok = lastToken(v).replace(/^@+/, '');
    if (timer.current) clearTimeout(timer.current);
    if (tok.length < 2) {
      setMatches([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const res = await client.call<{ match?: Array<{ email?: string; isGroup?: boolean }> }>(
          JSNS.mail,
          'AutoComplete',
          { name: tok },
        );
        let list: Suggestion[] = (res.match ?? [])
          .filter((m) => m.email)
          .slice(0, 8)
          .map((m) => ({ ...parseMatch(m.email as string), group: !!m.isGroup }));
        // Fallback: AutoComplete only knows contacts/GAL/sent-to. If it found
        // nothing, harvest matching addresses from people you've corresponded
        // with (incl. by domain, e.g. "@rsyd"), via a mail search.
        if (list.length === 0) {
          const low = tok.toLowerCase();
          const sr = (await client.mail.search({
            query: `from:${tok} OR to:${tok}`,
            types: 'message',
            sortBy: 'dateDesc',
            limit: 25,
          })) as { m?: Array<{ e?: Array<{ a?: string; p?: string; d?: string }> }> };
          const seen = new Set<string>();
          for (const m of sr.m ?? []) {
            for (const e of m.e ?? []) {
              const a = (e.a ?? '').toLowerCase();
              if (a && a.includes(low) && !seen.has(a)) {
                seen.add(a);
                list.push({ name: e.p ?? e.d ?? '', addr: e.a as string, group: false });
              }
            }
          }
          list = list.slice(0, 8);
        }
        setMatches(list);
        setHi(0);
        setOpen(list.length > 0);
      } catch {
        setMatches([]);
        setOpen(false);
      }
    }, 180);
  }

  function pick(m: Suggestion) {
    const i = value.lastIndexOf(',');
    const prefix = i >= 0 ? `${value.slice(0, i + 1)} ` : '';
    onChange(`${prefix}${m.addr}, `);
    setMatches([]);
    setOpen(false);
    inputRef.current?.focus();
  }

  function onKey(e: React.KeyboardEvent) {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const sel = matches[hi];
      if (sel) pick(sel);
    } else if (e.key === 'Escape') {
      e.preventDefault(); // close the suggestions only, not the compose window
      setOpen(false);
    }
  }

  return (
    <div className="rcpt">
      <input
        ref={inputRef}
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          runQuery(e.target.value);
        }}
        onKeyDown={onKey}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open ? (
        <div className="rcpt-menu">
          {matches.map((m, idx) => (
            <div
              key={`${m.addr}-${idx}`}
              className={`rcpt-item${idx === hi ? ' hi' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(m);
              }}
              onMouseEnter={() => setHi(idx)}
            >
              <span className="rcpt-name">
                {m.group ? '👥 ' : ''}
                {m.name || m.addr}
              </span>
              {m.name ? <span className="rcpt-addr">{m.addr}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const TOOLS: Array<{ cmd: string; label: string; arg?: boolean }> = [
  { cmd: 'bold', label: 'B' },
  { cmd: 'italic', label: 'I' },
  { cmd: 'underline', label: 'U' },
  { cmd: 'insertUnorderedList', label: '• List' },
  { cmd: 'insertOrderedList', label: '1. List' },
  { cmd: 'createLink', label: '🔗 Link', arg: true },
];

export function ComposeWindow({ init, onClose }: { init: ComposeInit; onClose: () => void }) {
  const { client, session } = useClient();
  const [to, setTo] = useState(init.to ?? '');
  const [cc, setCc] = useState(init.cc ?? '');
  const [subject, setSubject] = useState(init.subject ?? '');
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const orig = init.origAttachments ?? [];
  const origKey = (a: OrigAttachment) => `${a.messageId}/${a.part}`;
  // several messages in the thread carry attachments → label chips by sender
  const multiSource = new Set(orig.map((a) => a.messageId)).size > 1;
  // which of the thread's attachments to include: forwards carry the forwarded
  // message's own by default (as the classic client does); replies leave them
  // all out unless ticked.
  const [origIncluded, setOrigIncluded] = useState<Set<string>>(
    () =>
      new Set(
        init.draftId
          ? orig.map(origKey) // a reopened draft keeps every attachment it was saved with
          : init.inReplyTo?.replyType === 'w'
            ? orig.filter((a) => a.messageId === init.inReplyTo?.origId).map(origKey)
            : [],
      ),
  );
  // the draft this window writes to: the reopened one, or the one a first Save created
  const draftIdRef = useRef<string | undefined>(init.draftId);
  const isMobile = useIsMobile();
  // drag the window by its title bar so the mail list underneath stays visible
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const formRef = useRef<HTMLFormElement>(null);
  function startDrag(e: React.PointerEvent<HTMLDivElement>) {
    if (isMobile || e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    const el = formRef.current;
    if (!el) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    const start = { mx: e.clientX, my: e.clientY, x: pos.x, y: pos.y, left: r.left, top: r.top, w: r.width };
    const hdr = e.currentTarget;
    hdr.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      let dx = ev.clientX - start.mx;
      let dy = ev.clientY - start.my;
      // keep the title bar reachable: at least 80 px of it on screen, never above the top
      dx = Math.min(Math.max(dx, 80 - start.left - start.w), window.innerWidth - 80 - start.left);
      dy = Math.min(Math.max(dy, -start.top), window.innerHeight - 48 - start.top);
      setPos({ x: start.x + dx, y: start.y + dy });
    };
    const up = () => {
      hdr.removeEventListener('pointermove', move);
      hdr.removeEventListener('pointerup', up);
      hdr.removeEventListener('pointercancel', up);
    };
    hdr.addEventListener('pointermove', move);
    hdr.addEventListener('pointerup', up);
    hdr.addEventListener('pointercancel', up);
  }
  const [uploading, setUploading] = useState(false);
  const [delinger, setDelinger] = useState<DelingItem[]>([]);
  const delingRef = useRef<HTMLInputElement>(null);
  const lastRange = useRef<Range | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const send = useSend();
  const saveDraft = useSaveDraft();
  // "Save draft / Discard / Keep editing" prompt shown when closing unsent work
  const [closing, setClosing] = useState(false);
  const sig = useSignature();
  const seeded = useRef(false);
  const seededHtml = useRef('');

  // seed the editor once: signature (above) + any quoted reply/forward body.
  // wait until the signature query settles so it isn't dropped.
  useEffect(() => {
    if (seeded.current || !editorRef.current) return;
    if (init.draftId) {
      // a reopened draft: exactly as it was saved (its signature is already in it)
      seeded.current = true;
      editorRef.current.innerHTML = init.draftHtml ?? '';
      seededHtml.current = editorRef.current.innerHTML;
      return;
    }
    if (sig.isLoading) return;
    seeded.current = true;
    const quoted = init.quotedHtml
      ? `<br><br>${init.quotedHtml}`
      : init.text
        ? `<br><br>${escapeHtml(init.text).replace(/\n/g, '<br>')}`
        : '';
    const signature = sig.data ? `<br><br>-- <br>${sig.data}` : '';
    editorRef.current.innerHTML = `<br>${signature}${quoted}`;
    seededHtml.current = editorRef.current.innerHTML;
  }, [sig.isLoading, sig.data, init.text, init.quotedHtml, init.draftId, init.draftHtml]);

  /** true once the user has typed, attached or changed anything */
  function isDirty(): boolean {
    return (
      to !== (init.to ?? '') ||
      cc !== (init.cc ?? '') ||
      subject !== (init.subject ?? '') ||
      attachments.length > 0 ||
      delinger.length > 0 ||
      (!!init.draftId && origIncluded.size !== orig.length) ||
      (editorRef.current?.innerHTML ?? '') !== seededHtml.current
    );
  }
  /** close (✕ / Discard / Esc) — asks first when there is unsent work */
  function requestClose() {
    if (send.isPending || saveDraft.isPending) return;
    if (closing) {
      setClosing(false); // second Esc / ✕ = keep editing
      return;
    }
    if (isDirty()) setClosing(true);
    else onClose();
  }
  async function saveAndClose() {
    setError('');
    try {
      const res = await saveDraft.mutateAsync(message());
      draftIdRef.current = res.m?.[0]?.id ?? draftIdRef.current;
      onClose();
    } catch (err) {
      setClosing(false);
      setError(err instanceof Error ? err.message : 'Could not save draft');
    }
  }
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) requestCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // remember the caret inside the editor, so a finished Deling upload lands there
  useEffect(() => {
    const onSel = () => {
      const sel = document.getSelection();
      const ed = editorRef.current;
      if (!sel || sel.rangeCount === 0 || !ed) return;
      const r = sel.getRangeAt(0);
      if (ed.contains(r.startContainer)) lastRange.current = r.cloneRange();
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);

  const patchDeling = (id: number, p: Partial<DelingItem>) =>
    setDelinger((prev) => prev.map((d) => (d.id === id ? { ...d, ...p } : d)));

  /** Insert the link block at the caret, or at the top (above the signature) if never focused. */
  function insertDelingBlock(shareId: string, html: string) {
    const ed = editorRef.current;
    if (!ed) return;
    const block = document.createElement('div');
    block.setAttribute('data-deling', shareId);
    block.innerHTML = html;
    const r = lastRange.current;
    if (r && ed.contains(r.startContainer)) {
      r.collapse(false);
      r.insertNode(block);
    } else {
      ed.insertBefore(block, ed.firstChild);
    }
    const after = document.createRange();
    after.setStartAfter(block);
    after.collapse(true);
    lastRange.current = after;
  }

  async function shareViaDeling(f: File) {
    const id = ++delingSeq;
    setDelinger((prev) => [...prev, { id, file: f, pct: 0, status: 'uploading' }]);
    try {
      const cfg = await getDelingConfig(session.csrfToken);
      const up = uploadToDeling(f, cfg, (pct) => patchDeling(id, { pct }));
      patchDeling(id, { abort: up.abort });
      const r = await up.promise;
      insertDelingBlock(r.shareId, delingBlockHtml(r));
      patchDeling(id, { status: 'done', pct: 100, shareId: r.shareId, linkDays: cfg.linkDays, abort: undefined });
    } catch (err) {
      if (err instanceof Error && err.message === 'aborted') return;
      patchDeling(id, { status: 'error', error: err instanceof Error ? err.message : 'Upload failed', abort: undefined });
    }
  }

  function removeDeling(d: DelingItem) {
    d.abort?.();
    setDelinger((prev) => prev.filter((x) => x.id !== d.id));
    if (d.shareId) {
      editorRef.current?.querySelectorAll(`[data-deling="${CSS.escape(d.shareId)}"]`).forEach((n) => n.remove());
      void revokeDelingLink(d.shareId, session.csrfToken).catch(() => undefined);
    }
  }

  /** Small file that failed via Deling → attach it normally instead. */
  async function attachInstead(d: DelingItem) {
    setDelinger((prev) => prev.filter((x) => x.id !== d.id));
    await attachFiles([d.file]);
  }

  function exec(cmd: string, arg?: boolean) {
    if (arg) {
      const url = window.prompt('Link URL:');
      if (!url) return;
      document.execCommand(cmd, false, url);
    } else {
      document.execCommand(cmd);
    }
    editorRef.current?.focus();
  }

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>, viaDeling = false) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    if (!session.shareEnabled) return attachFiles(files);
    // files above the Deling threshold always go as a link
    let autoMb = 10;
    try {
      autoMb = (await getDelingConfig(session.csrfToken)).autoMb;
    } catch {
      /* the upload itself will report the error */
    }
    const big = (f: File) => viaDeling || f.size > autoMb * 1024 * 1024;
    files.filter(big).forEach((f) => void shareViaDeling(f));
    await attachFiles(files.filter((f) => !big(f)));
  }

  async function attachFiles(files: File[]) {
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      for (const f of files) {
        const uploaded = await uploadFile(f, session.csrfToken);
        setAttachments((prev) => [...prev, ...uploaded]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  /** the message as currently composed (shared by Send and Save draft) */
  function message(): SendVars {
    return {
      to: parseAddrs(to),
      cc: cc.trim() ? parseAddrs(cc) : undefined,
      subject,
      text: editorRef.current?.innerText ?? '',
      html: editorRef.current?.innerHTML ?? '',
      attachments: [
        ...attachments.map((a) => ({ uploadId: a.aid })),
        ...orig.filter((a) => origIncluded.has(origKey(a))).map((a) => ({ messageId: a.messageId, part: a.part })),
      ],
      inReplyTo: init.inReplyTo,
      draftId: draftIdRef.current,
    };
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const recipients = parseAddrs(to);
    if (recipients.length === 0) {
      setError('Add at least one recipient');
      return;
    }
    try {
      await send.mutateAsync(message());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
    }
  }

  return (
    // clicking outside does NOT close: a stray click (or a text selection
    // released outside the window) must never throw the message away
    <div className="compose-backdrop">
      <form
        className="compose"
        onSubmit={submit}
        ref={formRef}
        style={pos.x || pos.y ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : undefined}
      >
        <div className="compose-hdr" onPointerDown={startDrag} title={isMobile ? undefined : 'Drag to move'}>
          <span>{init.title ?? 'New Message'}</span>
          <button type="button" className="compose-x" onClick={requestClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="compose-field">
          <span>To</span>
          <RecipientField value={to} onChange={setTo} placeholder="name or email" autoFocus client={client} />
        </div>
        <div className="compose-field">
          <span>Cc</span>
          <RecipientField value={cc} onChange={setCc} placeholder="optional" client={client} />
        </div>
        <label className="compose-field">
          <span>Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </label>
        <div className="compose-toolbar">
          {TOOLS.map((t) => (
            <button
              key={t.cmd}
              type="button"
              className={t.cmd === 'bold' ? 'b' : t.cmd === 'italic' ? 'i' : t.cmd === 'underline' ? 'u' : ''}
              onMouseDown={(e) => {
                e.preventDefault();
                exec(t.cmd, t.arg);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="compose-editor" contentEditable ref={editorRef} data-placeholder="Write your message…" />
        {orig.length > 0 ? (
          <div className="attach-chips orig-attach">
            <span className="orig-attach-label">{init.draftId ? 'Attached' : 'In this thread'}</span>
            {orig.map((a) => {
              const k = origKey(a);
              const on = origIncluded.has(k);
              const who = multiSource && a.from ? `${a.from}${a.date ? `, ${fmtDay(a.date)}` : ''}` : '';
              return (
                <label
                  className={`chip orig-chip${on ? ' on' : ''}`}
                  key={k}
                  title={`${who ? `From ${who} — ` : ''}${on ? 'included' : 'not included, tick to include'}`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) =>
                      setOrigIncluded((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(k);
                        else next.delete(k);
                        return next;
                      })
                    }
                  />
                  📎 {a.filename}
                  {a.size ? <span className="chip-size">{fmtSize(a.size)}</span> : null}
                  {who ? <span className="chip-from">{a.from}</span> : null}
                </label>
              );
            })}
            {orig.length > 1 ? (
              <button
                type="button"
                className="orig-attach-all"
                onClick={() =>
                  setOrigIncluded(origIncluded.size === orig.length ? new Set() : new Set(orig.map(origKey)))
                }
              >
                {origIncluded.size === orig.length ? 'Include none' : 'Include all'}
              </button>
            ) : null}
          </div>
        ) : null}
        {attachments.length > 0 || delinger.length > 0 ? (
          <div className="attach-chips">
            {attachments.map((a, i) => (
              <span className="chip" key={`${a.aid}-${i}`}>
                📎 {a.filename}
                <button type="button" onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} aria-label="Remove">
                  ✕
                </button>
              </span>
            ))}
            {delinger.map((d) => (
              <span className={`chip deling-chip ${d.status}`} key={`deling-${d.id}`} title={d.error ?? d.file.name}>
                🔗 {d.file.name}
                {d.status === 'uploading' ? (
                  <span className="deling-progress" aria-label={`${d.pct}%`}>
                    <i style={{ width: `${d.pct}%` }} />
                    <em>{d.pct}%</em>
                  </span>
                ) : d.status === 'done' ? (
                  <span className="deling-note">Shared via link · {d.linkDays ?? 14} days</span>
                ) : (
                  <span className="deling-err">{d.error}</span>
                )}
                {d.status === 'error' && d.file.size <= 10 * 1024 * 1024 ? (
                  <button type="button" className="deling-fallback" onClick={() => void attachInstead(d)}>
                    Attach instead
                  </button>
                ) : null}
                <button type="button" onClick={() => removeDeling(d)} aria-label="Remove">
                  ✕
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => void onFiles(e)} />
        <input ref={delingRef} type="file" multiple hidden onChange={(e) => void onFiles(e, true)} />
        <div className="compose-foot">
          <span className="err">{error}</span>
          <div className="compose-actions">
            <button type="button" className="ghost" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : '📎 Attach'}
            </button>
            {session.shareEnabled ? (
              <button
                type="button"
                className="ghost deling-btn"
                title="Upload to Deling (share.mo2.dk) and insert a download link — for large files"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => delingRef.current?.click()}
              >
                🔗 Share via Deling
              </button>
            ) : null}
            <button type="button" className="ghost" onClick={requestClose}>
              Discard
            </button>
            <button type="submit" className="primary" disabled={send.isPending || uploading || delinger.some((d) => d.status === 'uploading')}>
              {send.isPending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
        {closing ? (
          <div className="compose-close-ask" role="dialog" aria-label="Close message">
            <div className="compose-close-box">
              <div className="compose-close-title">{draftIdRef.current ? 'Save your changes to this draft?' : 'Save this message as a draft?'}</div>
              <div className="compose-close-actions">
                <button type="button" className="ghost" onClick={() => setClosing(false)} disabled={saveDraft.isPending}>
                  Keep editing
                </button>
                <button type="button" className="ghost danger" onClick={onClose} disabled={saveDraft.isPending}>
                  Discard
                </button>
                <button type="button" className="primary" autoFocus onClick={() => void saveAndClose()} disabled={saveDraft.isPending}>
                  {saveDraft.isPending ? 'Saving…' : 'Save draft'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </div>
  );
}
